/* eslint-disable no-param-reassign */
import Vue from 'vue'
import Vuex from 'vuex'
import PadletApi from 'surface/padlet_api'
import reaction from 'surface/xstore/modules/reaction'
import section from 'surface/xstore/modules/section'
import comment from 'surface/xstore/modules/comment'
import postConnection from 'surface/xstore/modules/post_connection'
import post from 'surface/xstore/modules/post'
import postAction from 'surface/xstore/modules/post_action'
import table from 'surface/xstore/modules/table'
import share from 'surface/xstore/modules/share'
import settings from 'surface/xstore/modules/settings'
import privacySettings from 'surface/xstore/modules/privacy_settings'
import onboarding from 'surface/xstore/modules/onboarding'
import contentPicker from 'surface/xstore/modules/content_picker'
import confirmationDialog from 'surface/xstore/modules/confirmation_dialog'
import snackbars from 'surface/xstore/modules/snackbars'
import nativeApp from 'surface/xstore/modules/native_app'
import map from 'surface/xstore/modules/map'
import attachment from 'surface/xstore/modules/attachment'
import imagePicker from 'surface/xstore/modules/image_picker'
import logger from 'surface/xstore/plugins/logger'
import addressListener from 'surface/xstore/plugins/address_listener'
import { WallPrivacy } from '@padlet/wall-privacy'
import { getCompatibleThemeId } from 'bits/map_theme'
import { __ } from 'bits/intl'
import {
  likeWall,
  unlikeWall,
  saveWall,
  deleteWall,
} from '@padlet/arvo'

Vue.use(Vuex)
// How to decide what to put in state vs getters
// State should have either:
// 1. things we sync with the server
// 2. things that change
// 3. things that are a function of the device
// Everything else is in getters.

// Keep all lists alphabetical
const plugins = [addressListener]
if (process.env.NODE_ENV !== 'production') {
  plugins.push(logger)
}

export const blankState = () => ({
  /* BROWSER VARIABLES */
  isFullscreen: false,
  windowHeight: 0,
  windowWidth: 0,

  /* CONSTANTS */
  canPostMessage: false,
  constants: {},
  cookiesEnabled: false,
  csrfToken: '',
  deviceId: '',
  dpr: 1,
  environment: 'production',
  inlinePostEdit: false,
  isAndroid: false,
  isApp: false,
  isChrome: false,
  isDesktop: false,
  isEdge: false,
  isEmbedded: false,
  isFirefox: false,
  isIE: false,
  isIOS: false,
  isIPhone: false,
  isIPhoneX: false,
  isMac: false,
  isMobile: false,
  isMousable: false,
  isOldApp: false,
  isPhone: false,
  isSafari: false,
  isTablet: false,
  isTouchable: false,
  isWindows: false,
  locale: 'en',
  modalPostEdit: false,
  nativePostEdit: false,
  release: '',
  showAlertSheet: false,
  xHeader: false,

  /* OBJECTS */
  tenant: {},
  user: {},
  wall: {},

  /* OTHERS */
  commentsPanelPostId: null,
  convertLayoutErrorMessage: '',
  editorHeight: 0,
  headerHeight: 136,
  fileThatExceededSize: null,
  isLikedByCurrentUser: false,
  isOnline: true,
  wishListMounted: false,
  xContributorsList: false,
  xExtensionRequired: false,
  xFileSizeExceeded: false,
  xWallQuotaExceeded: false,

  /* PANELS */
  xClearAllDialog: false,
  xCommentsPanel: false,
  xConvertLayoutPanel: false,
  xCookieNotification: false,
  xDeleteDialog: false,
  xInfo: false,
  xPermitNotification: false,
  xReloadNotification: false,
  xRemakePanel: false,
  xToolMenu: false,

  /* PERMISSIONS */
  amIOwner: false,
  canIAdminister: false,
  canIEdit: false,
  canIModerate: false,
  canIRemake: false,
  canIWrite: false,
  isReadOnly: true,
})

const store = new Vuex.Store({
  plugins,
  modules: {
    attachment,
    nativeApp,
    imagePicker,
    reaction,
    postConnection,
    comment,
    section,
    post,
    postAction,
    table,
    share,
    settings,
    privacySettings,
    onboarding,
    contentPicker,
    confirmationDialog,
    map,
    snackbars,
  },
  state: blankState(),
  getters: {
    // constants
    mapThemesDictionary: (state) => state.constants.allMapThemes.reduce((obj, item) => {
      obj[item.id] = item
      return obj
    }, {}),
    // wall
    displayAttrs: (state, getters) => getters['settings/isPreviewing'] ? getters['settings/previewAttributes'] : state.wall,
    wallId: state => state.wall.id,
    wallUpdatedAt: state => state.wall.updated_at,
    wallCreatedAt: state => state.wall.created_at,
    originalBackground: (state) => state.wall.background,
    title: (state, getters) => getters.displayAttrs.title,
    description: (state, getters) => getters.displayAttrs.description,
    background: (state, getters) => getters.displayAttrs.background,
    portrait: (state, getters) => getters.displayAttrs.portrait,
    format: (state, getters) => getters.displayAttrs.viz,
    newPostLocation: (state, getters) => getters.displayAttrs.new_wish_loc,
    filterProfanity: (state, getters) => getters.displayAttrs.filter_profanity,
    requireApproval: (state, getters) => getters.displayAttrs.require_approval,
    backgroundColor: (state, getters) => getters.displayAttrs.background.dominant_color_as_rgb,
    backgroundLuminance: (state, getters) => getters.displayAttrs.background.luminance_name,
    isBackgroundBlurred: (state, getters) => getters.background.effect === 'blur',
    colorScheme: (state, getters) => getters.displayAttrs.color_scheme,
    themeId: (state, getters) => getters.displayAttrs.theme_id, // used for map
    mapTheme: (state, getters) => getters.mapThemesDictionary[getCompatibleThemeId(getters.themeId)],
    fontId: (state, getters) => getters.displayAttrs.font_id,
    isCommentable: (state, getters) => getters.displayAttrs.is_commentable,
    isReactable: (state, getters) => getters.displayAttrs.is_reactable,
    reactionData: (state, getters) => getters.displayAttrs.reaction_data,
    publicTagList: (state, getters) => getters.displayAttrs.public_tag_list,
    name: (state, getters) => getters.displayAttrs.name,
    xAuthor: (state, getters) => getters.displayAttrs.show_author,

    builder: state => state.wall.builder,
    namespace: state => state.wall.namespace,
    convertLayoutErrorMessage: state => state.convertLayoutErrorMessage,
    // synchronized wall privacy for remaking
    wallPrivacy: state => new WallPrivacy({
      ...state.wall.privacy_policy,
      ausers: state.wall.privacy_policy.users,
    }),
    contributors: (state, getters) => getters.wallPrivacy.contributors || [],
    access: (state, getters) => getters.wallPrivacy.access,
    isOrgWall: state => state.wall.tenant_id > 1,
    coverPostId: state => state.wall.cover_wish_id,
    publicKey: state => state.wall.public_key,
    address: state => state.wall.address,
    domainName: state => state.tenant.domain_name,
    tenantName: state => state.tenant.name,
    canIAdminister: state => state.canIAdminister,
    canIModerate: state => state.canIModerate,
    canIWrite: state => state.canIWrite,
    canIEdit: state => state.canIEdit,
    canIRemake: state => state.canIRemake,
    canIChangeCoverPost: state => state.canIAdminister,
    amIOwner: state => state.amIOwner,
    amIRegistered: state => !!state.user.registered_at,
    canICreateWall: state => state.user.quota.walls_used < state.user.quota.walls_limit,
    isCanvas: state => state.wall.viz === 'free',
    isBackchannel: state => state.wall.viz === 'chat',
    isMap: state => state.wall.viz === 'map',
    isReadOnly: (state) => state.isReadOnly,
    isPostable: (state, getters) => getters.canIWrite && !state.isReadOnly,
    isShelf: state => state.wall.viz === 'shelf',
    isGrid: state => state.wall.viz === 'grid',
    isTable: state => state.wall.viz === 'table',
    isSectioned: (state, getters) => getters.isShelf,
    canHaveConnections: (state, getters) => getters.isCanvas,
    xPostCommentsInPanel: (state, getters) => getters.isMap || getters.isTable,
    uploadLimit: state => state.wall.upload_limit,
    isPremiumWall: state => state.wall.is_premium, // wall made by a pro user
    isRemakeable: state => state.wall.is_remakeable,
    links: state => state.wall.links,
    xSharePanel: (state, getters) => getters['share/isOpen'],
    xSettingsPanel: (state, getters) => getters['settings/isOpen'],
    isAPanelVisible: (state, getters) => (
      getters.xSharePanel ||
      getters.xSettingsPanel ||
      getters['onboarding/xReadyToPostPanel'] ||
      getters['contentPicker/xContentPickerPanel'] ||
      state.reaction.postIdBeingReactedTo ||
      state.xRemakePanel ||
      state.xConvertLayoutPanel ||
      state.xInfo
    ),
    xHeader: (state) => state.xHeader,

    /* POST MODIFIERS */
    xTallAttachmentPickerBox: (state, getters) => getters.isMap,
  },
  mutations: {
    INITIALIZE_STATE (state, data) {
      Object.assign(state, data)
    },
    RESIZE_WINDOW (state, dimensions) {
      state.windowHeight = dimensions.height
      state.windowWidth = dimensions.width
    },
    CHANGE_CONNECTIVITY (state, data) {
      state.isOnline = data.isOnline
    },
    LIKE_WALL (state) {
      state.isLikedByCurrentUser = true
    },
    UNLIKE_WALL (state) {
      state.isLikedByCurrentUser = false
    },
    ENTER_FULLSCREEN (state) {
      state.isFullscreen = true
    },
    EXIT_FULLSCREEN (state) {
      state.isFullscreen = false
    },
    HIDE_CONTRIBUTORS_LIST (state) {
      state.xContributorsList = false
    },
    HIDE_EXTENSION_REQUIRED (state) {
      state.xExtensionRequired = false
    },
    HIDE_FILE_SIZE_EXCEEDED (state) {
      state.xFileSizeExceeded = false
      state.fileThatExceededSize = null
    },
    HIDE_INFO (state) {
      state.xInfo = false
    },
    HIDE_WALL_QUOTA_EXCEEDED (state) {
      state.xWallQuotaExceeded = false
    },
    HIDE_COMMENTS_PANEL (state) {
      state.xCommentsPanel = false
      state.commentsPanelPostId = null
    },
    HIDE_DELETE_DIALOG (state) {
      state.xDeleteDialog = false
    },
    HIDE_CLEAR_ALL_DIALOG (state) {
      state.xClearAllDialog = false
    },
    HIDE_PERMIT_NOTIFICATION (state) {
      state.xPermitNotification = false
    },
    SHOW_COOKIE_NOTIFICATION (state) {
      state.xCookieNotification = true
    },
    HIDE_COOKIE_NOTIFICATION (state) {
      state.xCookieNotification = false
    },
    SHOW_RELOAD_NOTIFICATION (state) {
      state.xReloadNotification = true
    },
    HIDE_RELOAD_NOTIFICATION (state) {
      state.xReloadNotification = false
    },
    SHOW_CONTRIBUTORS_LIST (state) {
      state.xContributorsList = true
    },
    SHOW_FILE_SIZE_EXCEEDED (state, file) {
      state.xFileSizeExceeded = true
      state.fileThatExceededSize = file
    },
    SHOW_EXTENSION_REQUIRED (state) {
      state.xExtensionRequired = true
    },
    SHOW_INFO (state) {
      state.xInfo = true
    },
    SHOW_WALL_QUOTA_EXCEEDED (state) {
      state.xWallQuotaExceeded = true
    },
    SHOW_COMMENTS_PANEL (state, postId) {
      state.xCommentsPanel = true
      state.commentsPanelPostId = postId
    },
    SHOW_DELETE_DIALOG (state) {
      state.xDeleteDialog = true
    },
    SHOW_CLEAR_ALL_DIALOG (state) {
      state.xClearAllDialog = true
    },
    SHOW_PERMIT_NOTIFICATION (state) {
      state.xPermitNotification = true
    },
    STARTING_NEW_SECTION (state, newSection) {
      state.newSection = newSection
    },
    RESIZE_EDITOR (state, height) {
      state.editorHeight = height
    },
    RESIZE_HEADER (state, height) {
      state.headerHeight = height
    },
    SHOW_CONVERT_LAYOUT_PANEL (state) {
      state.xConvertLayoutPanel = true
    },
    HIDE_CONVERT_LAYOUT_PANEL (state) {
      state.xConvertLayoutPanel = false
    },
    CONVERT_LAYOUT_FAILED (state, message) {
      state.convertLayoutErrorMessage = message
    },
    SHOW_TOOL_MENU (state) {
      state.xToolMenu = true
    },
    HIDE_TOOL_MENU (state) {
      state.xToolMenu = false
    },
    SHOW_REMAKE_PANEL (state) {
      state.xRemakePanel = true
    },
    HIDE_REMAKE_PANEL (state) {
      state.xRemakePanel = false
    },
    UPDATE_WALL (state, changes) {
      state.wall = { ...state.wall, ...changes }
    },
    ANNOUNCE_WISH_LIST_MOUNTED (state) {
      state.wishListMounted = true
    },
  },
  actions: {
    initializeState ({ commit, dispatch }, data) {
      const {
        isFirstView,
        expandedPostCid,
        ...rootStoreState
      } = data
      commit('INITIALIZE_STATE', rootStoreState)
      dispatch('onboarding/setIsFirstView', isFirstView)
      if (expandedPostCid) {
        dispatch('postAction/expandPost', { postCid: expandedPostCid })
      }
    },
    resizeWindow ({ commit }, dimensions) {
      commit('RESIZE_WINDOW', dimensions)
    },
    changeConnectivity ({ commit }, data) {
      commit('CHANGE_CONNECTIVITY', data)
    },
    /* ------------
     * PANEL
     * ------------ */
    hideContributorsList ({ commit }) {
      commit('HIDE_CONTRIBUTORS_LIST')
    },
    hideExtensionRequired ({ commit }) {
      commit('HIDE_EXTENSION_REQUIRED')
    },
    hideFileSizeExceeded ({ commit }) {
      commit('HIDE_FILE_SIZE_EXCEEDED')
    },
    hideInfo ({ commit }) {
      commit('HIDE_INFO')
    },
    hideWallQuotaExceeded ({ commit }) {
      commit('HIDE_WALL_QUOTA_EXCEEDED')
    },
    hideCommentsPanel ({ commit }) {
      commit('HIDE_COMMENTS_PANEL')
    },
    hideToolMenu ({ commit }) {
      commit('HIDE_TOOL_MENU')
    },
    hideDeleteDialog ({ commit }) {
      commit('HIDE_DELETE_DIALOG')
    },
    hideClearAllDialog ({ commit }) {
      commit('HIDE_CLEAR_ALL_DIALOG')
    },
    hidePermitNotification ({ commit }) {
      commit('HIDE_PERMIT_NOTIFICATION')
    },
    showCookieNotification ({ commit }) {
      commit('SHOW_COOKIE_NOTIFICATION')
    },
    hideCookieNotification ({ commit }) {
      commit('HIDE_COOKIE_NOTIFICATION')
    },
    showReloadNotification ({ commit }) {
      commit('SHOW_RELOAD_NOTIFICATION')
    },
    hideReloadNotification ({ commit }) {
      commit('HIDE_RELOAD_NOTIFICATION')
    },
    showContributorsList ({ commit }) {
      commit('SHOW_CONTRIBUTORS_LIST')
    },
    showExtensionRequired ({ commit }) {
      commit('SHOW_EXTENSION_REQUIRED')
    },
    showFileSizeExceeded ({ commit, dispatch }, { file }) {
      dispatch('post/rejectPostsWithFileSizeExceeded', { file })
      commit('SHOW_FILE_SIZE_EXCEEDED', file)
    },
    showInfo ({ commit }) {
      commit('SHOW_INFO')
    },
    showWallQuotaExceeded ({ commit }) {
      commit('SHOW_WALL_QUOTA_EXCEEDED')
    },
    showCommentsPanel ({ commit }, { postId }) {
      commit('SHOW_COMMENTS_PANEL', postId)
    },
    showDeleteDialog ({ commit }) {
      commit('SHOW_DELETE_DIALOG')
    },
    showClearAllDialog ({ commit }) {
      commit('SHOW_CLEAR_ALL_DIALOG')
    },
    showPermitNotification ({ commit }) {
      commit('SHOW_PERMIT_NOTIFICATION')
    },
    showConvertLayoutPanel ({ commit }) {
      commit('SHOW_CONVERT_LAYOUT_PANEL')
    },
    hideConvertLayoutPanel ({ commit }) {
      commit('HIDE_CONVERT_LAYOUT_PANEL')
    },
    async convertWallLayout ({ commit, getters }, { newLayout }) {
      try {
        await PadletApi.Wall.convertLayout({
          id: getters.wallId,
          newLayout,
        })
        commit('UPDATE_WALL', { viz: newLayout })
      } catch {
        const errorMessage = __('Failed to convert layout. Please try again.')
        commit('CONVERT_LAYOUT_FAILED', errorMessage)
      }
    },
    showRemakePanel ({ commit }) {
      commit('SHOW_REMAKE_PANEL')
    },
    hideRemakePanel ({ commit }) {
      commit('HIDE_REMAKE_PANEL')
    },
    toggleToolMenu ({ commit, state }) {
      if (state.xToolMenu) {
        commit('HIDE_TOOL_MENU')
      } else {
        commit('SHOW_TOOL_MENU')
      }
    },
    /* ------------
     * POST
     * ------------ */
    removePost ({ dispatch }, { id, cid }) {
      dispatch('post/removePost', { id, cid })
    },
    addPost ({ dispatch }, payload) {
      const postAttributes = payload.post
      dispatch('post/addPost', { postAttributes })
    },
    /* ------------
     * ATTACHMENT
     * ------------ */
    resizeEditor ({ commit }, { height }) {
      commit('RESIZE_EDITOR', height)
    },

    /* ------------
     * HEADER
     * ------------ */
    resizeHeader ({ commit }, { height }) {
      commit('RESIZE_HEADER', height)
    },

    /* ------------
     * WALL
     * ------------ */
    updateWall ({ commit }, changes) {
      commit('UPDATE_WALL', changes)
    },
    async likeWall ({ commit, getters }) {
      await likeWall(getters.wallId)
      commit('LIKE_WALL')
    },
    async unlikeWall ({ commit, getters }) {
      await unlikeWall(getters.wallId)
      commit('UNLIKE_WALL')
    },
    async setCoverPost ({ commit, getters }, { postId }) {
      const changes = { cover_wish_id: postId }
      await saveWall({ id: getters.wallId, ...changes })
      commit('UPDATE_WALL', changes)
    },
    async unsetCoverPost ({ commit, getters }) {
      const changes = { cover_wish_id: null }
      await saveWall({ id: getters.wallId, ...changes })
      commit('UPDATE_WALL', changes)
    },
    enterFullscreen ({ commit }) {
      commit('ENTER_FULLSCREEN')
    },
    exitFullscreen ({ commit }) {
      commit('EXIT_FULLSCREEN')
    },
    async deleteWall ({ getters }) {
      await deleteWall(getters.wallId)
      window.location = '/'
    },

    // SCROLL
    announceWishListMounted ({ commit }) {
      commit('ANNOUNCE_WISH_LIST_MOUNTED')
    },
    /* ------------
     * POST CONNECTION
     * ------------ */
    removeConnectionsForPost ({ dispatch }, { postId }) {
      dispatch('postConnection/removeConnectionsForPost', { postId })
    },
    /* ------------
     * REALTIME
     * ------------ */
    NEW_COMMENT_REMOTE ({ dispatch }, payload) {
      dispatch('comment/newCommentRemote', payload.commentAttributes)
    },
    EDIT_COMMENT_REMOTE ({ dispatch }, payload) {
      dispatch('comment/editCommentRemote', payload.commentAttributes)
    },
    DELETE_COMMENT_REMOTE ({ dispatch }, payload) {
      dispatch('comment/deleteCommentRemote', payload.commentAttributes)
    },
    NEW_POST_REMOTE ({ dispatch }, payload) {
      dispatch('post/newPostRemote', payload.postAttributes)
    },
    EDIT_POST_REMOTE ({ dispatch }, payload) {
      dispatch('post/editPostRemote', payload.postAttributes)
    },
    EDIT_WALL_REMOTE (context, payload) {},
    DELETE_WALL_REMOTE (context, payload) {},
    respondToRemoteUpdates ({ dispatch }, { type, payload }) {
      // Right now we are calling 'remote' actions in the modules themselves.
      // Eventually, the modules will have no concept of remote updates.
      // They'll only have simple CRUD operations that'll be called by this guy.
      if (type === 'deleteWish') {
        dispatch('post/deletePostRemote', payload)
        dispatch('postAction/deletePostRemote', payload)
      } else if (type === 'addConnection') {
        dispatch('postConnection/newConnectionRemote', payload)
      } else if (type === 'deleteConnection') {
        dispatch('postConnection/deleteConnectionRemote', payload)
      } else if (type === 'addReaction') {
        dispatch('reaction/newReactionRemote', payload)
      } else if (type === 'editReaction') {
        dispatch('reaction/editReactionRemote', payload)
      } else if (type === 'deleteReaction') {
        dispatch('reaction/deleteReactionRemote', payload)
      } else if (type === 'addSection') {
        dispatch('section/newSectionRemote', payload)
      } else if (type === 'editSection') {
        dispatch('section/editSectionRemote', payload)
      } else if (type === 'deleteSection') {
        dispatch('section/deleteSectionRemote', payload)
      } else if (type === 'editWall') {
        dispatch('updateWall', payload)
      }
    },
    refresh ({ dispatch, getters }) {
      const mapWasFittingMarkerBounds = getters['map/mapDoesFitMarkersBounds']
      dispatch('map/disableMapFittingMarkersBounds')
      dispatch('reaction/fetchReactions')
      dispatch('comment/fetchComments')
      dispatch('post/fetchPosts').then(() => {
        dispatch('postAction/recalibrate')
        if (mapWasFittingMarkerBounds) {
          dispatch('map/enableMapFittingMarkersBounds')
        }
      })
      if (getters.isSectioned) {
        dispatch('section/fetchSections')
      }
      if (getters.canHaveConnections) {
        dispatch('postConnection/fetchConnections')
      }
    },
  },
})

const oldDispatch = (obj) => store.dispatch(obj.type, obj)
/**
 * The old dispatch function signature is different from the vuex store dispatch function signature
 * We write the dispatch function in ww.xstore so that any method signature will work.
 * ```js
 * ww.xstore.dispatch({type: 'START_EDITING_POST', payload: {postCid: id}})
 * ww.xstore.dispatch('START_EDITING_POST', {postCid: id})
 * ww.xstore.dispatch(function (dispatch) => {dispatch({type: 'START_EDITING_POST', payload: {postCid: id}})}
 * ```
 */
window.ww.xstore = {
  store: store,
  state: {},
  getters: {},
  dispatch: (obj, payload) => {
    if (typeof obj === 'string') {
      store.dispatch(obj, payload)
    } else if (obj.type) {
      oldDispatch(obj)
    } else if (typeof obj === 'function') {
      obj(oldDispatch)
    }
  },
}

store.subscribe(() => {
  window.ww.xstore.state = store.state
  window.ww.xstore.getters = store.getters
})

export default store
